import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { getPhaseData } from '../../data/programData';
import {
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Clock,
  Calendar,
  Dumbbell,
  CheckCircle,
} from 'lucide-react';

export function PhaseProgressPage() {
  const { phaseId } = useParams();
  const { user, aclCare } = useAuth();

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSession, setExpandedSession] = useState(null);

  const phaseNum = parseInt(phaseId);
  const phaseData = getPhaseData(phaseNum);
  const phaseKey = `phase_${phaseNum}`;
  const userPhaseData = aclCare?.phases?.[phaseKey];

  // Fetch training sessions for this phase
  useEffect(() => {
    async function fetchSessions() {
      if (!user?.id) return;

      setLoading(true);
      try {
        const result = await api.getTrainingSessions(user.id, { phase: phaseNum });
        const sessionList = result.items || result || [];
        // Sort by completed_at descending (most recent first)
        sessionList.sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at));
        setSessions(sessionList);
      } catch (err) {
        console.error('[PhaseProgressPage] Error fetching sessions:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchSessions();
  }, [user?.id, phaseNum]);

  // Get target completions from programData
  const targetCompletions = phaseData?.targetCompletions || { session_1: 8, session_2: 8, optional: 4 };

  // Get actual completions from user data
  const completions = {
    session_1: userPhaseData?.completions?.session_1 || 0,
    session_2: userPhaseData?.completions?.session_2 || 0,
    optional: userPhaseData?.completions?.optional || 0,
  };

  const getProgress = () => {
    // Only count session 1 and session 2 for progress (not optional)
    const completed = completions.session_1 + completions.session_2;
    const total = targetCompletions.session_1 + targetCompletions.session_2;

    return {
      completed,
      total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0
    };
  };

  const progress = getProgress();

  const getWorkoutLabel = (workout) => {
    switch (workout) {
      case 1: return 'Session 1';
      case 2: return 'Session 2';
      case 3: return 'Optional';
      default: return 'Session';
    }
  };

  const getWorkoutColor = (workout) => {
    switch (workout) {
      case 1: return 'bg-blue-100 text-blue-700';
      case 2: return 'bg-green-100 text-green-700';
      case 3: return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(Number(timestamp));
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(Number(timestamp));
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const toggleSession = (sessionId) => {
    setExpandedSession(expandedSession === sessionId ? null : sessionId);
  };

  return (
    <div className="max-w-2xl mx-auto p-4 pb-24 lg:pb-8">
      {/* Header */}
      <div className="flex items-center mb-6">
        <TrendingUp className="h-6 w-6 text-primary mr-2" />
        <h1 className="text-2xl font-bold text-gray-900">
          {phaseData?.title} Progress
        </h1>
      </div>

      {/* Overall Progress Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-4">
        <div className="text-center mb-6">
          <div className="text-4xl font-bold text-primary">{progress.percentage}%</div>
          <p className="text-gray-500">Overall Completion</p>
          <p className="text-sm text-gray-400 mt-1">
            {progress.completed} of {progress.total} sessions completed
          </p>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-3 mb-6">
          <div
            className="h-3 rounded-full bg-primary transition-all"
            style={{ width: `${progress.percentage}%` }}
          />
        </div>

        {/* Session Breakdown */}
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-2xl font-semibold text-blue-700">
              {completions.session_1}
            </p>
            <p className="text-xs text-blue-600">
              of {targetCompletions.session_1}
            </p>
            <p className="text-sm font-medium text-blue-800 mt-1">Session 1</p>
          </div>

          <div className="p-3 bg-green-50 rounded-lg">
            <p className="text-2xl font-semibold text-green-700">
              {completions.session_2}
            </p>
            <p className="text-xs text-green-600">
              of {targetCompletions.session_2}
            </p>
            <p className="text-sm font-medium text-green-800 mt-1">Session 2</p>
          </div>

          <div className="p-3 bg-purple-50 rounded-lg">
            <p className="text-2xl font-semibold text-purple-700">
              {completions.optional}
            </p>
            <p className="text-xs text-purple-600">
              of {targetCompletions.optional}
            </p>
            <p className="text-sm font-medium text-purple-800 mt-1">Optional*</p>
          </div>
        </div>

        {/* Note about optional */}
        <p className="text-xs text-gray-400 text-center mt-3">
          * Optional sessions are not included in progress calculation
        </p>
      </div>

      {/* Session History */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">Session History</h2>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-gray-500 text-sm">Loading sessions...</p>
          </div>
        ) : sessions.length === 0 ? (
          <div className="p-8 text-center">
            <Dumbbell className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No sessions completed yet</p>
            <p className="text-sm text-gray-400 mt-1">
              Complete your first workout to see it here
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {sessions.map((session) => (
              <div key={session.id} className="bg-white">
                {/* Session Header */}
                <button
                  onClick={() => toggleSession(session.id)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                      <CheckCircle className="h-5 w-5 text-primary" />
                    </div>
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {getWorkoutLabel(session.workout)}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getWorkoutColor(session.workout)}`}>
                          {getWorkoutLabel(session.workout)}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-gray-500 mt-0.5">
                        <span className="flex items-center">
                          <Calendar className="h-3.5 w-3.5 mr-1" />
                          {formatDate(session.completed_at)}
                        </span>
                        <span className="flex items-center">
                          <Clock className="h-3.5 w-3.5 mr-1" />
                          {session.duration_minutes} min
                        </span>
                      </div>
                    </div>
                  </div>
                  {expandedSession === session.id ? (
                    <ChevronUp className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  )}
                </button>

                {/* Expanded Exercise Details */}
                {expandedSession === session.id && (
                  <div className="px-4 pb-4 bg-gray-50">
                    <div className="space-y-3">
                      {session.exercises?.supersets?.map((superset, idx) => (
                        <SupersetDetail key={superset.superset_id || idx} superset={superset} index={idx} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Superset Detail Component
function SupersetDetail({ superset, index }) {
  const [expanded, setExpanded] = useState(false);

  // Get the first set's data as representative
  const set1 = superset.sets?.set1;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-3 flex items-center justify-between hover:bg-gray-50"
      >
        <div className="flex items-center">
          <div className="h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center mr-2 text-xs font-medium text-gray-600">
            {index + 1}
          </div>
          <div className="text-left">
            <p className="text-sm font-medium text-gray-900">{superset.exercise1_name}</p>
            <p className="text-xs text-gray-500">{superset.exercise2_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {superset.completed && (
            <CheckCircle className="h-4 w-4 text-green-500" />
          )}
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 border-t border-gray-100">
          {/* Exercise 1 Data */}
          <div className="mt-3">
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">
              {superset.exercise1_name}
            </p>
            <ExerciseDataDisplay data={set1?.exercise1} />
          </div>

          {/* Exercise 2 Data */}
          <div className="mt-3">
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">
              {superset.exercise2_name}
            </p>
            <ExerciseDataDisplay data={set1?.exercise2} />
          </div>

          {/* All Sets Summary */}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500 uppercase mb-2">All Sets</p>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(superset.sets || {}).map(([setKey, setData]) => (
                <SetSummary key={setKey} setKey={setKey} setData={setData} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Exercise Data Display Component
function ExerciseDataDisplay({ data }) {
  if (!data) return <p className="text-sm text-gray-400">No data recorded</p>;

  const displayItems = [];

  // Handle different data structures
  if (data.reps !== undefined) {
    displayItems.push({ label: 'Reps', value: data.reps });
  }
  if (data.weight !== undefined && data.weight !== '') {
    displayItems.push({ label: 'Weight', value: `${data.weight} lbs` });
  }
  if (data.level !== undefined && data.level !== '') {
    displayItems.push({ label: 'Level', value: data.level });
  }
  if (data.bandColor !== undefined && data.bandColor !== '') {
    displayItems.push({ label: 'Band', value: data.bandColor });
  }
  if (data.holdTime !== undefined) {
    displayItems.push({ label: 'Hold Time', value: `${data.holdTime}s` });
  }

  // Ladder drills
  if (data.drill1 || data.drill2 || data.drill3) {
    const drills = [data.drill1, data.drill2, data.drill3].filter(Boolean);
    displayItems.push({ label: 'Drills', value: drills.join(', ') });
  }

  // Left/Right data
  if (data.left || data.right) {
    if (data.left?.reps !== undefined) {
      displayItems.push({ label: 'Left Reps', value: data.left.reps });
    }
    if (data.right?.reps !== undefined) {
      displayItems.push({ label: 'Right Reps', value: data.right.reps });
    }
    if (data.left?.holdTime !== undefined) {
      displayItems.push({ label: 'Left Hold', value: `${data.left.holdTime}s` });
    }
    if (data.right?.holdTime !== undefined) {
      displayItems.push({ label: 'Right Hold', value: `${data.right.holdTime}s` });
    }
    if (data.left?.boxHeight !== undefined && data.left.boxHeight !== '') {
      displayItems.push({ label: 'Left Box Height', value: data.left.boxHeight });
    }
    if (data.right?.boxHeight !== undefined && data.right.boxHeight !== '') {
      displayItems.push({ label: 'Right Box Height', value: data.right.boxHeight });
    }
  }

  // Notes
  if (data.notes && data.notes.trim()) {
    displayItems.push({ label: 'Notes', value: data.notes });
  }

  if (displayItems.length === 0) {
    return <p className="text-sm text-gray-400">Completed</p>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {displayItems.map((item, idx) => (
        <div key={idx} className="bg-gray-100 rounded px-2 py-1">
          <span className="text-xs text-gray-500">{item.label}: </span>
          <span className="text-xs font-medium text-gray-700">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

// Set Summary Component
function SetSummary({ setKey, setData }) {
  const setNum = setKey.replace('set', '');

  // Get a quick summary of what was documented
  const ex1 = setData?.exercise1;
  const ex2 = setData?.exercise2;

  const getSummary = (data) => {
    if (!data) return '—';
    if (data.reps) return `${data.reps} reps`;
    if (data.left?.reps && data.right?.reps) return `L:${data.left.reps} R:${data.right.reps}`;
    if (data.holdTime) return `${data.holdTime}s`;
    if (data.left?.holdTime && data.right?.holdTime) return `L:${data.left.holdTime}s R:${data.right.holdTime}s`;
    if (data.drill1) return 'Drills ✓';
    return '✓';
  };

  return (
    <div className="bg-gray-50 rounded p-2 text-center">
      <p className="text-xs font-medium text-gray-600 mb-1">Set {setNum}</p>
      <p className="text-xs text-gray-500">{getSummary(ex1)}</p>
      <p className="text-xs text-gray-500">{getSummary(ex2)}</p>
    </div>
  );
}